import * as ContextMenuPrimitive from "@radix-ui/react-context-menu";
import { Check, ChevronRight, Circle } from "lucide-react";
import * as React from "react";

import { cn } from "../lib/utils";

// ─── Size variant via context ───────────────────────────────────────────────
//
// `<ContextMenuContent size="sm">` propagates a smaller density to every
// descendant Item / RadioItem / CheckboxItem / Label / Separator without
// each call site having to repeat the className. Use the default (`"default"`)
// for action menus that do real operations; use `"sm"` for quick property
// tweaks where the labels are short and the menu shouldn't dominate the viewport.

export type ContextMenuSize = "default" | "sm";

const SizeCtx = React.createContext<ContextMenuSize>("default");
const useContextMenuSize = () => React.useContext(SizeCtx);

const ContextMenu = ContextMenuPrimitive.Root;

const ContextMenuTrigger = ContextMenuPrimitive.Trigger;

const ContextMenuGroup = ContextMenuPrimitive.Group;

const ContextMenuPortal = ContextMenuPrimitive.Portal;

const ContextMenuSub = ContextMenuPrimitive.Sub;

const ContextMenuRadioGroup = ContextMenuPrimitive.RadioGroup;

const ContextMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.SubTrigger> & {
    inset?: boolean;
  }
>(({ className, inset, children, ...props }, ref) => {
  const size = useContextMenuSize();
  return (
    <ContextMenuPrimitive.SubTrigger
      ref={ref}
      className={cn(
        "flex cursor-default select-none items-center gap-2 rounded-sm outline-none focus:bg-accent data-[state=open]:bg-accent [&_svg]:pointer-events-none [&_svg]:shrink-0",
        size === "sm"
          ? "px-1.5 py-0.5 text-xs [&_svg]:size-3.5"
          : "px-2 py-1.5 text-sm [&_svg]:size-4",
        inset && (size === "sm" ? "pl-6" : "pl-8"),
        className,
      )}
      {...props}
    >
      {children}
      <ChevronRight className="ml-auto" />
    </ContextMenuPrimitive.SubTrigger>
  );
});
ContextMenuSubTrigger.displayName = ContextMenuPrimitive.SubTrigger.displayName;

const ContextMenuSubContent = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => {
  const size = useContextMenuSize();
  return (
    <ContextMenuPrimitive.SubContent
      ref={ref}
      className={cn(
        "z-50 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-context-menu-content-transform-origin]",
        size === "sm" ? "min-w-[6rem] p-0.5" : "min-w-[8rem] p-1",
        className,
      )}
      {...props}
    />
  );
});
ContextMenuSubContent.displayName = ContextMenuPrimitive.SubContent.displayName;

// Note: ContextMenuContent does NOT accept `sideOffset`, `side`, or `align` —
// Radix omits them because context menu content always positions at the cursor.
const ContextMenuContent = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Content> & {
    size?: ContextMenuSize;
  }
>(({ className, size = "default", ...props }, ref) => (
  <ContextMenuPrimitive.Portal>
    <SizeCtx.Provider value={size}>
      <ContextMenuPrimitive.Content
        ref={ref}
        className={cn(
          "z-50 max-h-[var(--radix-context-menu-content-available-height)] overflow-y-auto overflow-x-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-context-menu-content-transform-origin]",
          size === "sm" ? "min-w-[6rem] p-0.5" : "min-w-[8rem] p-1",
          className,
        )}
        {...props}
      />
    </SizeCtx.Provider>
  </ContextMenuPrimitive.Portal>
));
ContextMenuContent.displayName = ContextMenuPrimitive.Content.displayName;

const ContextMenuItem = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Item> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => {
  const size = useContextMenuSize();
  return (
    <ContextMenuPrimitive.Item
      ref={ref}
      className={cn(
        "relative flex cursor-default select-none items-center gap-2 rounded-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        size === "sm"
          ? "px-1.5 py-0.5 text-xs [&_svg]:size-3.5"
          : "px-2 py-1.5 text-sm [&_svg]:size-4",
        inset && (size === "sm" ? "pl-6" : "pl-8"),
        className,
      )}
      {...props}
    />
  );
});
ContextMenuItem.displayName = ContextMenuPrimitive.Item.displayName;

const ContextMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => {
  const size = useContextMenuSize();
  return (
    <ContextMenuPrimitive.CheckboxItem
      ref={ref}
      className={cn(
        "relative flex cursor-default select-none items-center rounded-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        size === "sm" ? "py-0.5 pl-6 pr-1.5 text-xs" : "py-1.5 pl-8 pr-2 text-sm",
        className,
      )}
      checked={checked}
      {...props}
    >
      <span
        className={cn(
          "absolute flex items-center justify-center",
          size === "sm" ? "left-1.5 h-3 w-3" : "left-2 h-3.5 w-3.5",
        )}
      >
        <ContextMenuPrimitive.ItemIndicator>
          <Check className={size === "sm" ? "h-3 w-3" : "h-4 w-4"} />
        </ContextMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </ContextMenuPrimitive.CheckboxItem>
  );
});
ContextMenuCheckboxItem.displayName = ContextMenuPrimitive.CheckboxItem.displayName;

const ContextMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => {
  const size = useContextMenuSize();
  return (
    <ContextMenuPrimitive.RadioItem
      ref={ref}
      className={cn(
        "relative flex cursor-default select-none items-center rounded-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        size === "sm" ? "py-0.5 pl-6 pr-1.5 text-xs" : "py-1.5 pl-8 pr-2 text-sm",
        className,
      )}
      {...props}
    >
      <span
        className={cn(
          "absolute flex items-center justify-center",
          size === "sm" ? "left-1.5 h-3 w-3" : "left-2 h-3.5 w-3.5",
        )}
      >
        <ContextMenuPrimitive.ItemIndicator>
          <Circle className={cn("fill-current", size === "sm" ? "h-1.5 w-1.5" : "h-2 w-2")} />
        </ContextMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </ContextMenuPrimitive.RadioItem>
  );
});
ContextMenuRadioItem.displayName = ContextMenuPrimitive.RadioItem.displayName;

const ContextMenuLabel = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Label> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => {
  const size = useContextMenuSize();
  return (
    <ContextMenuPrimitive.Label
      ref={ref}
      className={cn(
        "font-semibold",
        size === "sm"
          ? "px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground"
          : "px-2 py-1.5 text-sm",
        inset && (size === "sm" ? "pl-6" : "pl-8"),
        className,
      )}
      {...props}
    />
  );
});
ContextMenuLabel.displayName = ContextMenuPrimitive.Label.displayName;

const ContextMenuSeparator = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Separator>
>(({ className, ...props }, ref) => {
  const size = useContextMenuSize();
  return (
    <ContextMenuPrimitive.Separator
      ref={ref}
      className={cn("h-px bg-muted", size === "sm" ? "-mx-0.5 my-0.5" : "-mx-1 my-1", className)}
      {...props}
    />
  );
});
ContextMenuSeparator.displayName = ContextMenuPrimitive.Separator.displayName;

const ContextMenuShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span className={cn("ml-auto text-xs tracking-widest opacity-60", className)} {...props} />
  );
};
ContextMenuShortcut.displayName = "ContextMenuShortcut";

export {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuCheckboxItem,
  ContextMenuRadioItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuGroup,
  ContextMenuPortal,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuRadioGroup,
};
